import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

const PWAContext = createContext(null);

export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  const isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const checkStandalone = useCallback(() => {
    if (typeof window === 'undefined') return false;

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    setIsStandalone(standalone);

    return standalone;
  }, []);

  useEffect(() => {
    checkStandalone();

    const handleBeforeInstallPrompt = (event) => {
      // Oprim prompt-ul automat al browserului.
      // Îl vom afișa când utilizatorul apasă butonul nostru.
      event.preventDefault();

      console.log('PWA: beforeinstallprompt disponibil');

      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      console.log('PWA: aplicația a fost instalată');

      setDeferredPrompt(null);
    };

    const mediaQuery = window.matchMedia(
      '(display-mode: standalone)'
    );

    const handleDisplayModeChange = () => {
      checkStandalone();
    };

    window.addEventListener(
      'beforeinstallprompt',
      handleBeforeInstallPrompt
    );

    window.addEventListener(
      'appinstalled',
      handleAppInstalled
    );

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener(
        'change',
        handleDisplayModeChange
      );
    } else {
      mediaQuery.addListener(handleDisplayModeChange);
    }

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );

      window.removeEventListener(
        'appinstalled',
        handleAppInstalled
      );

      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener(
          'change',
          handleDisplayModeChange
        );
      } else {
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, [checkStandalone]);

  const installApp = async () => {
    if (!deferredPrompt) {
      return {
        available: false,
        outcome: null,
      };
    }

    try {
      await deferredPrompt.prompt();

      const choiceResult = await deferredPrompt.userChoice;

      console.log(
        'PWA: rezultat instalare:',
        choiceResult.outcome
      );

      // Evenimentul beforeinstallprompt poate fi folosit o singură dată.
      setDeferredPrompt(null);

      return {
        available: true,
        outcome: choiceResult.outcome,
      };
    } catch (error) {
      console.error(
        'PWA: eroare la afișarea instalării:',
        error
      );

      setDeferredPrompt(null);

      return {
        available: true,
        outcome: 'error',
      };
    }
  };

  const canInstall =
    !isStandalone &&
    deferredPrompt !== null;

  return (
    <PWAContext.Provider
      value={{
        isStandalone,
        canInstall,
        isIOS,
        installApp,
      }}
    >
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = () => {
  const context = useContext(PWAContext);

  if (!context) {
    throw new Error(
      'usePWA trebuie folosit în interiorul PWAProvider'
    );
  }

  return context;
};

export default PWAContext;